import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {UploadFlowType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {canSchemaHaveMods} from 'molbio/OligoUtil';
import * as ModalContainer from 'ui-library/ModalContainer';

import ChooseImportLocationModalPane from './panes/ChooseImportLocationModalPane';
import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
  DerivedColumnsStoreKeys,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  SpreadsheetPropTypes,
  TaskState,
  VALUE_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';

export interface BulkUpdateBioentitiesFlowProps {
  initialFolder: {folderId: string | null; registryId: string | null} | null;
  complexPolymerSchemaConfigurations?: Array<ComplexPolymerSchemaConfigGraphQL>;
  onRequestClose: () => void;
}

class BulkUpdateBioentitiesFlow extends React.Component<BulkUpdateBioentitiesFlowProps> {
  static propTypes = {
    initialFolder: SpreadsheetPropTypes.selectedDestination,
    complexPolymerSchemaConfigurations: PropTypes.array,
    onRequestClose: PropTypes.func.isRequired,
  };

  static open(props: Omit<BulkUpdateBioentitiesFlowProps, 'onRequestClose'>): void {
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkUpdateBioentitiesFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  private getValidColumnTypes = (options: {selectedSchemaIds: SelectedSchemaIds}): Array<COLUMN_TYPE> => {
    const validColumns = [
      COLUMN_TYPE.FILENAME,
      COLUMN_TYPE.NEW_BIOENTITY_NAME,
      COLUMN_TYPE.NEW_REGISTRY_ID,
      ...SpreadsheetBulkImportUtil.getPolymerColumnTypes(
        SpreadsheetBulkImportUtil.shouldHaveResidues(options.selectedSchemaIds),
        canSchemaHaveMods(SpreadsheetBulkImportUtil.getTagSchema(options.selectedSchemaIds)!)
      ),
      ...SpreadsheetBulkImportUtil.getMoleculeColumnTypes(
        SpreadsheetBulkImportUtil.shouldHaveChemicalStructure(options.selectedSchemaIds)
      ),
      ...SpreadsheetBulkImportUtil.getComplexPolymerColumnTypes(
        options.selectedSchemaIds,
        this.props.complexPolymerSchemaConfigurations
      ),
      ...VALUE_COLUMN_TYPES,
    ];
    if (BenchlingConfig.REGISTRY_ENABLE_BULK_ENTITY_FOLDER_CHANGE) {
      validColumns.push(COLUMN_TYPE.FOLDER_PATH_FORWARD_SLASH);
    }
    return validColumns;
  };

  private getRequiredColumnsError = (
    columns: Array<SpreadsheetColumn>,
    {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
  ): string | null => {
    const requiredColumnTypes = [COLUMN_TYPE.FILENAME];
    const requiredFieldError = SpreadsheetBulkImportUtil.getRequiredFieldError(
      requiredColumnTypes,
      columns,
      selectedSchemaIds,
      IMPORT_TYPE.UPDATE_BIOENTITIES
    );
    const errorForChemicalStructureColumns = SpreadsheetBulkImportUtil.getErrorForChemicalStructureColumns(
      columns,
      SpreadsheetBulkImportUtil.shouldHaveChemicalStructure(selectedSchemaIds)
    );

    if (requiredFieldError) {
      return requiredFieldError;
    } else if (errorForChemicalStructureColumns) {
      return errorForChemicalStructureColumns;
    } else if (!columns.some(({type}) => type !== COLUMN_TYPE.FILENAME)) {
      return 'At least one other field is required.';
    } else {
      return null;
    }
  };

  _getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_DESTINATION,
      configuration: ChooseImportLocationModalPane.configure({
        showRegistries: true,
        showInventory: true,
        folderSelectorLabel: 'Update location',
        folderSelectorPopoverContent: 'Only entities in this location will be updated.',
        shouldFilterToComplexPolymerSchemas: false,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure(),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes: this.getValidColumnTypes,
        getRequiredColumnsError: this.getRequiredColumnsError,
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForUpdateBioentities,
        savedMappingsConfig: {
          enableSavedMappings: true,
          uploadFlow: UploadFlowType.BULK_UPDATE_ENTITIES,
        },
        customHandleGoToNextPaneFn: (paneProps) => {
          if (SpreadsheetBulkImportUtil.shouldCheckDerivedColumns(paneProps.fileData.columns)) {
            SpreadsheetBulkImportActions.findDerivedColumns();
            SpreadsheetBulkImportActions.goToNextPane();
          } else {
            paneProps.onValidateSpreadsheet();
            SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.SHOW_ERRORS);
          }
        },
      }),
    },
    {
      paneKey: PaneKey.MAP_DERIVED_COLUMNS,
      configuration: {
        columnTransformConfigs: DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS,
        derivedColumnsKey: DerivedColumnsStoreKeys.BULK_UPDATE,
        onUpdateDerivedColumns: SpreadsheetBulkImportActions.setDerivedColumns,
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForUpdateBioentities,
      },
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        customHandleGoToPrevPaneFn: (paneProps) => {
          if (
            paneProps.derivedColumnsData.state === TaskState.COMPLETED &&
            paneProps.derivedColumnsData.derivedColumns &&
            paneProps.derivedColumnsData.derivedColumns.length > 0
          ) {
            SpreadsheetBulkImportActions.goToPrevPane();
          } else {
            SpreadsheetBulkImportActions.goToPaneWithKey(PaneKey.MAP_COLUMN_NAMES);
          }
        },
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
        uploadFlow: UploadFlowType.BULK_UPDATE_ENTITIES,
      }),
    },
  ];

  render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="entity"
        importType={IMPORT_TYPE.UPDATE_BIOENTITIES}
        initialSettings={{initialFolder: this.props.initialFolder}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.updateBioentitiesFromSpreadsheetWithDataMods}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Bulk update entities"
        complexPolymerSchemaConfigurations={this.props.complexPolymerSchemaConfigurations}
      />
    );
  }
}

export default BulkUpdateBioentitiesFlow;
